import { Injectable, Optional } from '@angular/core';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { EnumMeasureTypes } from '@library/store/measures/measures.interface';
import { ModulesFacade } from '@library/store/modules/modules.facade';
import { Module, SourceType } from '@library/store/modules/modules.interface';
import { RoomsFacade } from '@library/store/rooms/rooms.facade';
import { Room } from '@library/store/rooms/rooms.interface';
import { isDefined } from '@library/utils/helpers/store/is-defined';
import { TariffOptions } from '@library/utils/interfaces/tariff.interface';
import { LangPipe } from '@library/utils/pipes/lang.pipe';
import { YScaleType } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-sidebar/cp-compare-sidebar.service';
import { ControlPageSidebarCacheService, SidebarCacheDataset } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-sidebar/control-page-sidebar-cache.service';
import { ConsumptionUnit, ControlPageStore, DatasetCard, GraphDataset, GraphModes, GraphType } from '@pages/control-page/control-page-store.service';
import { FAVORITE_TEMPERATURE_MODULES, SUPPORTED_CONTROL_ENERGY_MODULES, SUPPORTED_CONTROL_WEATHER_MODULES } from '@pages/control-page/control-page.const';
import { Enums } from 'constants/Enums.constant';
import { firstValueFrom, map } from 'rxjs';

@Injectable()
export class ControlPageCardService {

  showHomeCard$ = this.modulesFacade.currentHomeModules$.pipe(
    map(modules => modules.some(m => ([Enums.ModuleType.NLG, Enums.ModuleType.NLE].includes(m.type)))),
  );

  showModeControlCard$ = this.modulesFacade.currentHomeModules$.pipe(
    map(modules => modules.some(m => SUPPORTED_CONTROL_ENERGY_MODULES.includes(m.type)))
  );

  rooms$ = this.roomsFacade.currentHomeRooms$;

  roomModules = (roomId: string) => this.modulesFacade.getCurrentHomeModulesByRoomId(roomId).pipe(
    map(modules => {
      modules = modules.filter(m => ([
        ...SUPPORTED_CONTROL_WEATHER_MODULES,
        ...SUPPORTED_CONTROL_ENERGY_MODULES,
      ].includes(m.type)));
      return modules;
    }),
  );

  constructor(
    private homesfacade: HomesFacade,
    private modulesFacade: ModulesFacade,
    private roomsFacade: RoomsFacade,
    @Optional() private controlPageStore: ControlPageStore,
    @Optional() private cacheService: ControlPageSidebarCacheService,
    private langPipe: LangPipe,
  ) { }

  async selectHomeElec(unit: ConsumptionUnit) {
    const home = await firstValueFrom(this.homesfacade.currentHome$);
    const schedule = await firstValueFrom(this.homesfacade.electricitySchedule$);
    const isPeakOffPeakTariff = schedule && schedule.tariff_option === TariffOptions.PEAK_OFF_PEAK;
    let datasets: GraphDataset[];
    let graphType: GraphType;

    if (isPeakOffPeakTariff) {
      graphType = GraphType.ELEC_PEAK_OFFPEAK;
      datasets = [
        {
          measureType: unit === ConsumptionUnit.CONSUMPTION
            ? EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_OFFPEAK
            : EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_OFFPEAK,
          home: home,
          label: 'legrand-app.__LEG_OFFPEAKTIME',
          hidden: false,
          yScaleType: unit === ConsumptionUnit.CONSUMPTION
            ? YScaleType.ELECTRICITY
            : YScaleType.PRICE,
          type: 'bar',
          color: '--slight-blue',
        },
        {
          measureType: unit === ConsumptionUnit.CONSUMPTION
            ? EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PEAK
            : EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_PEAK,
          home: home,
          label: 'legrand-app.__LEG_PEAKTIME',
          hidden: false,
          yScaleType: unit === ConsumptionUnit.CONSUMPTION
            ? YScaleType.ELECTRICITY
            : YScaleType.PRICE,
          type: 'bar',
          color: '--blue',
        },
      ];
    }
    else {
      graphType = GraphType.ELEC;
      datasets = [
        {
          measureType: unit === ConsumptionUnit.CONSUMPTION
            ? EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_BASE
            : EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_BASE,
          home: home,
          label: 'legrand-app.__ELECTRICITY',
          hidden: false,
          yScaleType: unit === ConsumptionUnit.CONSUMPTION
            ? YScaleType.ELECTRICITY
            : YScaleType.PRICE,
          type: 'bar',
          color: '--blue',
        },
      ];
    }

    this.cacheService.setDataset({
      homeId: home.id,
      selected: true,
      graphType: graphType,
      unit: unit,
    });

    this.controlPageStore.setOptions({graphMode: GraphModes.DEFAULT});
    this.controlPageStore.selectHomeConsumptionUnit(unit);

    this.controlPageStore.setCard({
      home: home,
      graphType: graphType,
      datasets: {
        [GraphModes.DEFAULT]: datasets,
      },
    });
  }

  async selectHomeWater(unit: ConsumptionUnit) {
    const home = await firstValueFrom(this.homesfacade.currentHome$);
    const modules = await firstValueFrom(this.modulesFacade.currentHomeModules$);
    const hotWaterModule = modules.find(m => m.source_type === SourceType.HOT_WATER);
    const coldWaterModule = modules.find(m => m.source_type === SourceType.COLD_WATER);

    if (!hotWaterModule && !coldWaterModule) {
      return;
    }

    this.cacheService.setDataset({
      homeId: home.id,
      selected: true,
      graphType: GraphType.WATER,
      unit: unit,
    });

    const datasets = [
      {
        measureType: unit === ConsumptionUnit.CONSUMPTION
          ? EnumMeasureTypes.SUM_FLUID_CONSUMPTION_HOT_WATER
          : EnumMeasureTypes.SUM_FLUID_PRICE_HOT_WATER,
        module: hotWaterModule,
        label: 'legrand-app.__HOT_WATER',
        hidden: false,
        yScaleType: YScaleType.WATER,
        type: 'bar',
        color: '--salmon-pink',
      },
      {
        measureType: unit === ConsumptionUnit.CONSUMPTION
          ? EnumMeasureTypes.SUM_FLUID_CONSUMPTION_COLD_WATER
          : EnumMeasureTypes.SUM_FLUID_PRICE_COLD_WATER,
        module: coldWaterModule,
        label: 'legrand-app.__COLD_WATER',
        hidden: false,
        yScaleType: YScaleType.WATER,
        type: 'bar',
        color: '--blue',
      },
    ] as GraphDataset[];

    this.controlPageStore.setOptions({graphMode: GraphModes.DEFAULT});
    this.controlPageStore.selectHomeConsumptionUnit(unit);

    this.controlPageStore.setCard({
      home: home,
      graphType: GraphType.WATER,
      datasets: {
        [GraphModes.DEFAULT]: datasets,
      },
    });
  }

  async selectHomeGas(unit: ConsumptionUnit) {
    const home = await firstValueFrom(this.homesfacade.currentHome$);
    const module = await firstValueFrom(this.modulesFacade.currentHomeModules$.pipe(
      map(modules => modules.find(m => m.source_type === SourceType.GAS)),
    ));

    if (!module) {
      return;
    }

    this.cacheService.setDataset({
      homeId: home.id,
      selected: true,
      graphType: GraphType.GAS,
      unit: unit,
    });

    const datasets = [{
      measureType: unit === ConsumptionUnit.CONSUMPTION
        ? EnumMeasureTypes.SUM_FLUID_CONSUMPTION_GAS
        : EnumMeasureTypes.SUM_FLUID_PRICE_GAS,
      module: module,
      label: 'legrand-app.__GAS',
      hidden: false,
      yScaleType: YScaleType.GAS,
      type: 'bar',
      color: '--blue',
    }] as GraphDataset[];

    this.controlPageStore.setOptions({graphMode: GraphModes.DEFAULT});
    this.controlPageStore.selectHomeConsumptionUnit(unit);

    this.controlPageStore.setCard({
      home: home,
      graphType: GraphType.GAS,
      datasets: {
        [GraphModes.DEFAULT]: datasets,
      },
    });
  }

  async selectTemperature(module: Module) {
    let card: DatasetCard;
    const room = await firstValueFrom(this.roomsFacade.getRoomById(module.room_id));

    if (SUPPORTED_CONTROL_ENERGY_MODULES.includes(module.type)) {
      card = {
        module: module,
        datasets: {
          [GraphModes.DEFAULT]: [
            {
              room,
              measureType: EnumMeasureTypes.TEMPERATURE,
              label: 'common-app.__TEMPERATURE',
              hidden: false,
              yScaleType: YScaleType.TEMPERATURE,
              type: 'line',
              color: '--blue',
            }
          ],
          [GraphModes.MINMAX]: [
            {
              room,
              measureType: EnumMeasureTypes.MAX_TEMPERATURE,
              label: this.langPipe.transform('common-app.__TEMPERATURE') + ' ' + this.langPipe.transform('station-app.__DASHBOARD_TITLE_MAXIMUM_TEMPERATURE').toLowerCase(),
              hidden: false,
              yScaleType: YScaleType.TEMPERATURE,
              type: 'line',
              color: '--red-alert',
            },
            {
              room,
              measureType: EnumMeasureTypes.MIN_TEMPERATURE,
              label: this.langPipe.transform('common-app.__TEMPERATURE') + ' ' + this.langPipe.transform('station-app.__DASHBOARD_TITLE_MINIMUM_TEMPERATURE').toLowerCase(),
              hidden: false,
              yScaleType: YScaleType.TEMPERATURE,
              type: 'line',
              color: '--blue',
            },
          ],
        },
        graphType: GraphType.TEMPERATURE
      };
    }
    else {
      card = {
        module: module,
        datasets: {
          [GraphModes.DEFAULT]: [
            {
              module, measureType: EnumMeasureTypes.TEMPERATURE,
              label: 'common-app.__TEMPERATURE',
              hidden: false,
              yScaleType: YScaleType.TEMPERATURE,
              type: 'line',
              color: '--blue',
            }
          ],
          [GraphModes.MINMAX]: [
            {
              module, measureType: EnumMeasureTypes.MAX_TEMPERATURE,
              label: this.langPipe.transform('common-app.__TEMPERATURE') + ' ' + this.langPipe.transform('station-app.__DASHBOARD_TITLE_MAXIMUM_TEMPERATURE').toLowerCase(),
              hidden: false,
              yScaleType: YScaleType.TEMPERATURE,
              type: 'line',
              color: '--red-alert',
            },
            {
              module, measureType: EnumMeasureTypes.MIN_TEMPERATURE,
              label: this.langPipe.transform('common-app.__TEMPERATURE') + ' ' + this.langPipe.transform('station-app.__DASHBOARD_TITLE_MINIMUM_TEMPERATURE').toLowerCase(),
              hidden: false,
              yScaleType: YScaleType.TEMPERATURE,
              type: 'line',
              color: '--blue',
            },
          ],
        },
        graphType: GraphType.TEMPERATURE
      };
    }

    this.selectCard(card);
  }

  selectHumidity(module: Module) {
    this.selectCard({
      module: module,
      graphType: GraphType.HUMIDITY,
      datasets: {
        [GraphModes.DEFAULT]: [
          {
            module,
            measureType: EnumMeasureTypes.HUMIDITY,
            label: 'common-app.__HUMIDITY',
            hidden: false,
            yScaleType: YScaleType.PERCENTAGE,
            type: 'line',
            color: '--blue',
          },
        ],
        [GraphModes.MINMAX]: [
          {
            module,
            measureType: EnumMeasureTypes.MAX_HUMIDITY,
            label: this.langPipe.transform('common-app.__HUMIDITY') + ' ' + this.langPipe.transform('station-app.__DASHBOARD_TITLE_MAXIMUM_TEMPERATURE').toLowerCase(),
            hidden: false,
            yScaleType: YScaleType.PERCENTAGE,
            type: 'line',
            color: '--red-alert',
          },
          {
            module,
            measureType: EnumMeasureTypes.MIN_HUMIDITY,
            label: this.langPipe.transform('common-app.__HUMIDITY') + ' ' + this.langPipe.transform('station-app.__DASHBOARD_TITLE_MINIMUM_TEMPERATURE').toLowerCase(),
            hidden: false,
            yScaleType: YScaleType.PERCENTAGE,
            type: 'line',
            color: '--blue',
          },
        ],
      }
    });
  }

  selectPressure(module: Module) {
    this.controlPageStore.setOptions({graphMode: GraphModes.DEFAULT});
    this.selectCard({
      module: module,
      graphType: GraphType.PRESSURE,
      datasets: {
        [GraphModes.DEFAULT]: [
          {
            module,
            measureType: EnumMeasureTypes.PRESSURE,
            label: 'common-app.__PRESSURE',
            hidden: false,
            yScaleType: YScaleType.PRESSURE,
            type: 'line',
            color: '--blue',
          }
        ],
        [GraphModes.MINMAX]: [
          {
            module,
            measureType: EnumMeasureTypes.PRESSURE,
            label: 'common-app.__PRESSURE',
            hidden: false,
            yScaleType: YScaleType.PRESSURE,
            type: 'line',
            color: '--blue',
          }
        ],
      }
    });
  }

  selectCO2(module: Module) {
    this.selectCard({
      module: module,
      graphType: GraphType.CO2,
      datasets: {
        [GraphModes.DEFAULT]: [
          {
            module,
            measureType: EnumMeasureTypes.CO2,
            label: 'common-settings.__DATA_CO2',
            hidden: false,
            yScaleType: YScaleType.PPM,
            type: 'line',
            color: '--blue',
          },
        ],
        [GraphModes.MINMAX]: [
          {
            module,
            measureType: EnumMeasureTypes.MAX_CO2,
            label: this.langPipe.transform('common-settings.__DATA_CO2') + ' ' + this.langPipe.transform('station-app.__DASHBOARD_TITLE_MAXIMUM_TEMPERATURE').toLowerCase(),
            hidden: false,
            yScaleType: YScaleType.PPM,
            type: 'line',
            color: '--red-alert',
          },
          {
            module,
            measureType: EnumMeasureTypes.MIN_CO2,
            label: this.langPipe.transform('common-settings.__DATA_CO2') + ' ' + this.langPipe.transform('station-app.__DASHBOARD_TITLE_MINIMUM_TEMPERATURE').toLowerCase(),
            hidden: false,
            yScaleType: YScaleType.PPM,
            type: 'line',
            color: '--blue',
          },
        ],
      }
    });
  }

  selectNoise(module: Module) {
    this.selectCard({
      module: module,
      graphType: GraphType.NOISE,
      datasets: {
        [GraphModes.DEFAULT]: [{
          module,
          measureType: EnumMeasureTypes.NOISE,
          label: 'common-settings.__DATA_NOISE',
          hidden: false,
          yScaleType: YScaleType.NOISE,
          type: 'line',
          color: '--blue',
        }],
        [GraphModes.MINMAX]: [
          {
            module,
            measureType: EnumMeasureTypes.MAX_NOISE,
            label: this.langPipe.transform('common-settings.__DATA_NOISE') + ' ' + this.langPipe.transform('station-app.__DASHBOARD_TITLE_MAXIMUM_TEMPERATURE').toLowerCase(),
            hidden: false,
            yScaleType: YScaleType.NOISE,
            type: 'line',
            color: '--red-alert',
          },
          {
            module,
            measureType: EnumMeasureTypes.MIN_NOISE,
            label: this.langPipe.transform('common-settings.__DATA_NOISE') + ' ' + this.langPipe.transform('station-app.__DASHBOARD_TITLE_MINIMUM_TEMPERATURE').toLowerCase(),
            hidden: false,
            yScaleType: YScaleType.NOISE,
            type: 'line',
            color: '--blue',
          },
        ],
      }
    });
  }

  selectWind(module: Module) {
    this.controlPageStore.setOptions({graphMode: GraphModes.DEFAULT});
    this.selectCard({
      module: module,
      graphType: GraphType.WIND,
      datasets: {
        [GraphModes.DEFAULT]: [
          {
            module,
            measureType: EnumMeasureTypes.WIND_STRENGTH,
            label: 'legrand-app.__WIND_SPEED',
            hidden: false,
            yScaleType: YScaleType.SPEED,
            type: 'line',
            color: '--blue',
          },
          {
            module,
            measureType: EnumMeasureTypes.GUST_STRENGTH,
            label: 'station-app.__GUST_SPEED',
            hidden: false,
            yScaleType: YScaleType.SPEED,
            type: 'line',
            color: '--grey-50',
          },
          {
            module,
            measureType: EnumMeasureTypes.WIND_ANGLE,
            label: 'common-app.__WIND_DIRECTION',
            hidden: false,
            yScaleType: YScaleType.WIND_ANGLE,
            type: 'wind-angle',
            color: '--red-alert',
          },
        ],
      }
    });
  }

  selectRain(module: Module) {
    this.controlPageStore.setOptions({graphMode: GraphModes.DEFAULT});
    this.selectCard({
      module: module,
      graphType: GraphType.RAIN,
      datasets: {
        [GraphModes.DEFAULT]: [{
          module,
          measureType: EnumMeasureTypes.SUM_RAIN,
          label: 'common-settings.__DATA_RAIN',
          hidden: false,
          yScaleType: YScaleType.RAIN,
          type: 'bar',
          color: '--blue',
        }],
        [GraphModes.MINMAX]: [{
          module,
          measureType: EnumMeasureTypes.SUM_RAIN,
          label: 'common-settings.__DATA_RAIN',
          hidden: false,
          yScaleType: YScaleType.RAIN,
          type: 'bar',
          color: '--blue-10',
        }],
      }
    });
  }

  async selectTherm(room: Room) {
    const defaultDatasets: GraphDataset[] = [
      {
        room,
        measureType: EnumMeasureTypes.TEMPERATURE,
        label: 'station-settings.__INDOOR_TEMP',
        hidden: false,
        yScaleType: YScaleType.TEMPERATURE,
        type: 'line',
        color: '--blue',
      },
      {
        room,
        measureType: EnumMeasureTypes.SP_TEMPERATURE,
        label: 'legrand-app.__TEMPERATURE_SETPOINT',
        hidden: false,
        yScaleType: YScaleType.TEMPERATURE,
        type: 'stepped-line',
        color: '--grey-50',
      },
    ];
    const minMaxDatasets: GraphDataset[] = [
      {
        room,
        measureType: EnumMeasureTypes.MIN_TEMPERATURE,
        label: this.langPipe.transform('station-settings.__INDOOR_TEMP') + ' ' + this.langPipe.transform('station-app.__DASHBOARD_TITLE_MINIMUM_TEMPERATURE').toLowerCase(),
        hidden: false,
        yScaleType: YScaleType.TEMPERATURE,
        type: 'line',
        color: '--blue',
      },
      {
        room,
        measureType: EnumMeasureTypes.MAX_TEMPERATURE,
        label: this.langPipe.transform('station-settings.__INDOOR_TEMP') + ' ' + this.langPipe.transform('station-app.__DASHBOARD_TITLE_MAXIMUM_TEMPERATURE').toLowerCase(),
        hidden: false,
        yScaleType: YScaleType.TEMPERATURE,
        type: 'line',
        color: '--red-alert',
      },
      {
        room,
        measureType: EnumMeasureTypes.SP_TEMPERATURE,
        label: 'legrand-app.__TEMPERATURE_SETPOINT',
        hidden: false,
        yScaleType: YScaleType.TEMPERATURE,
        type: 'stepped-line',
        color: '--grey-50',
      },
    ];

    const modules = await firstValueFrom(this.roomModules(room.id));
    const thermostat = modules.find(module => module.type === Enums.ModuleType.NATHERM1);
    if (thermostat) {
      const boilerOn: GraphDataset = {
        module: thermostat,
        measureType: EnumMeasureTypes.SUM_BOILER_ON,
        label: 'energie-app.__COURBES_BOILER_ON',
        hidden: false,
        yScaleType: YScaleType.PERCENTAGE,
        type: 'bar',
        color: '--ruby-pink-50',
      };
      defaultDatasets.push(boilerOn);
      minMaxDatasets.push(boilerOn);
    }
    else {
      const heatingPower: GraphDataset = {
        room,
        measureType: EnumMeasureTypes.HEATING_POWER_REQUEST,
        label: 'energie-app.__COURBES_HEATING_POWER',
        hidden: false,
        yScaleType: YScaleType.PERCENTAGE,
        type: 'bar',
        color: '--ruby-pink-50',
      };
      defaultDatasets.push(heatingPower);
      minMaxDatasets.push(heatingPower);
    }

    const homeModules = await firstValueFrom(this.modulesFacade.currentHomeModules$);
    const outdoorModule = homeModules.find(module => module.type === Enums.ModuleType.NAMODULE1);
    if (outdoorModule) {
      defaultDatasets.push({
        module: outdoorModule,
        measureType: EnumMeasureTypes.TEMPERATURE,
        label: 'energie-app.__COURBES_OUTDOOR',
        hidden: false,
        yScaleType: YScaleType.TEMPERATURE,
        type: 'line',
        color: '--see-green',
      });
      minMaxDatasets.push({
        module: outdoorModule,
        measureType: EnumMeasureTypes.TEMPERATURE,
        label: 'energie-app.__COURBES_OUTDOOR',
        hidden: false,
        yScaleType: YScaleType.TEMPERATURE,
        type: 'line',
        color: '--see-green',
      });
    }

    this.selectCard({
      room: room,
      datasets: {
        [GraphModes.DEFAULT]: defaultDatasets,
        [GraphModes.MINMAX]: minMaxDatasets,
      },
      graphType: GraphType.THERM
    });
  }

  async selectGraphTypeModule(graphType: GraphType, module: Module) {

    switch (graphType) {
      case GraphType.TEMPERATURE:
      case GraphType.THERM:
        switch (module.type) {
          case Enums.ModuleType.NAMAIN:
          case Enums.ModuleType.NAMODULE1:
          case Enums.ModuleType.NAMODULE4:
            this.selectTemperature(module);
            break;
          case Enums.ModuleType.NATHERM1:
          case Enums.ModuleType.NRV: {
            const room = await firstValueFrom(this.roomsFacade.getRoomById(module.room_id));
            this.selectTherm(room);
            break;
          }
        }
        break;
      case GraphType.HUMIDITY:
        this.selectHumidity(module);
        break;
      case GraphType.RAIN:
        this.selectRain(module);
        break;
      case GraphType.WIND:
        this.selectWind(module);
        break;
      case GraphType.PRESSURE:
        this.selectPressure(module);
        break;
      case GraphType.NOISE:
        this.selectNoise(module);
        break;
      case GraphType.CO2:
        this.selectCO2(module);
        break;
    }
  }

  private async selectCard(card: DatasetCard) {
    const homeId = card.module ? card.module.homeId : card.room.homeId;
    const dataset: SidebarCacheDataset = {
      homeId: homeId,
      selected: true,
      graphType: card.graphType,
    };
    dataset.moduleId = card.module?.id ?? undefined;
    dataset.roomId = card.room?.id ?? undefined;
    this.cacheService.setDataset(dataset);

    this.controlPageStore.setCard(card);
  }

  supportGraphModes(graphType: GraphType) {
    return [
      GraphType.TEMPERATURE,
      GraphType.NOISE,
      GraphType.CO2,
      GraphType.HUMIDITY,
      GraphType.THERM,
    ].includes(graphType);
  }

  /**
   * Selects the first card to display in the graph upon entering the webapp
   * or when changing home.
   *
   * Selects the last selected card in previous session
   *
   * if it's the first visite, selects the elec graphs if there are home cards
   *
   * If there is no home cards we select the first module of the first room displayed
   * in the sidebar.
   * We select:
   *  - temperature if it's a station or an outdoor module
   *  - therm if it's a thermostat or a valve
   */
  async selectFirstCard() {
    const home = await firstValueFrom(this.homesfacade.currentHome$.pipe(isDefined()));

    const dataset = this.cacheService.datasets[home.id];

    if (dataset && dataset.selected) {

      if (dataset.moduleId) {
        const [module] = await firstValueFrom(this.modulesFacade.getModulesById([dataset.moduleId]));
        switch (dataset.graphType) {
          case GraphType.TEMPERATURE:
            this.selectTemperature(module);
            break;
          case GraphType.HUMIDITY:
            this.selectHumidity(module);
            break;
          case GraphType.RAIN:
            this.selectRain(module);
            break;
          case GraphType.WIND:
            this.selectWind(module);
            break;
          case GraphType.PRESSURE:
            this.selectPressure(module);
            break;
          case GraphType.NOISE:
            this.selectNoise(module);
            break;
          case GraphType.CO2:
            this.selectCO2(module);
            break;
        }
      }
      else if (dataset.roomId) {
        const rooms = await firstValueFrom(this.roomsFacade.rooms$);
        const room = rooms.find(r => r.id === dataset.roomId);
        switch (dataset.graphType) {
          case GraphType.THERM:
            this.selectTherm(room);
            break;
        }
      }
      else if (dataset.homeId) {
        switch (dataset.graphType) {
          case GraphType.ELEC:
          case GraphType.ELEC_PEAK_OFFPEAK:
            this.selectHomeElec(dataset.unit);
            break;
          case GraphType.WATER:
            this.selectHomeWater(dataset.unit);
            break;
          case GraphType.GAS:
            this.selectHomeGas(dataset.unit);
            break;
        }
      }
    }
    else {

      const showHomeCard = await firstValueFrom(this.showHomeCard$);

      if (showHomeCard) {
        this.selectHomeElec(ConsumptionUnit.CONSUMPTION);
      }
      else {

        const rooms = await firstValueFrom(this.rooms$);

        for (const room of rooms) {
          let firstRoomModules = await firstValueFrom(this.roomModules(room.id));
          if (home.mode === 'favorite') {
            firstRoomModules = firstRoomModules.filter(module => {
              return FAVORITE_TEMPERATURE_MODULES.includes(module.type);
            });
          }

          if (firstRoomModules?.length > 0) {
            const module = firstRoomModules[0];
            let isSelected = false;

            switch (module.type) {
              case Enums.ModuleType.NAMAIN:
              case Enums.ModuleType.NAMODULE1:
                this.selectTemperature(module);
                isSelected = true;
                break;
              case Enums.ModuleType.NATHERM1:
              case Enums.ModuleType.NRV:
                await this.selectTherm(room);
                isSelected = true;
                break;
            }

            if (isSelected) {
              break;
            }
          }
        }
      }

    }
  }

}
